import { useState } from 'react';
import { styled } from 'styled-components';

import { Accordion } from 'components/Accordion/Accordion';
import { AccordionItem } from 'components/Accordion/Item/AccordionItem';
import { Icon } from 'components/Icon/Icon';
import type { ApiOrg } from 'modules/listing/api/org/types/ApiOrg';
import { colors, spacing } from 'theme/theme';

import { ViewOrgPageListingsTabsTabLabel } from './ViewOrgPageListingsTabsLabel';
import { ViewOrgPageTruncatedListings } from './components/ViewOrgPageTruncatedListings';
import type { ViewOrgPageListingTab } from './types/ViewOrgPageListingTab';

type Props = {
  org: ApiOrg;
  defaultTab: ViewOrgPageListingTab;
};

const StyledSummary = styled.summary`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${spacing[12]};
  padding-bottom: ${spacing[12]};
`;

const StyledAccordionItem = styled(AccordionItem)`
  border-bottom: 1px solid ${colors.calloutGrey};
`;

export function ViewOrgPageListingsTabsMobile({ org, defaultTab }: Props) {
  const [currentTab, setCurrentTab] = useState<ViewOrgPageListingTab | null>(
    defaultTab,
  );

  return (
    <Accordion>
      {org.jobs.length > 0 && (
        <StyledAccordionItem
          defaultOpen
          name="opportunities"
          onToggle={(open) =>
            setCurrentTab((prevTab) => {
              if (open) return 'JOB';
              if (prevTab === 'JOB') return null;
              return prevTab;
            })
          }
          header={
            <StyledSummary>
              <ViewOrgPageListingsTabsTabLabel
                $active={currentTab === 'JOB'}
                count={org.jobs.length}
                icon={<Icon name="briefcase" colorName="white" size={24} />}
              >
                {getText('Jobs')}
              </ViewOrgPageListingsTabsTabLabel>
              <Icon
                name={`chevron-${currentTab === 'JOB' ? 'up' : 'down'}`}
                colorName="darkContentGrey"
                size={24}
              />
            </StyledSummary>
          }
        >
          <ViewOrgPageTruncatedListings listingType="JOB" listings={org.jobs} />
        </StyledAccordionItem>
      )}

      {org.internships.length > 0 && (
        <StyledAccordionItem
          defaultOpen={defaultTab === 'INTERNSHIP'}
          name="opportunities"
          onToggle={(open) =>
            setCurrentTab((prevTab) => {
              if (open) return 'INTERNSHIP';
              if (prevTab === 'INTERNSHIP') return null;
              return prevTab;
            })
          }
          header={
            <StyledSummary>
              <ViewOrgPageListingsTabsTabLabel
                $active={currentTab === 'INTERNSHIP'}
                count={org.internships.length}
                icon={<Icon name="sign-path" colorName="white" size={24} />}
              >
                {getText('Internships')}
              </ViewOrgPageListingsTabsTabLabel>
              <Icon
                name={`chevron-${currentTab === 'INTERNSHIP' ? 'up' : 'down'}`}
                colorName="black"
                size={24}
              />
            </StyledSummary>
          }
        >
          <ViewOrgPageTruncatedListings
            listingType="INTERNSHIP"
            listings={org.internships}
          />
        </StyledAccordionItem>
      )}

      {org.volops.length > 0 && (
        <StyledAccordionItem
          defaultOpen={defaultTab === 'VOLOP'}
          name="opportunities"
          onToggle={(open) =>
            setCurrentTab((prevTab) => {
              if (open) return 'VOLOP';
              if (prevTab === 'VOLOP') return null;
              return prevTab;
            })
          }
          header={
            <StyledSummary>
              <ViewOrgPageListingsTabsTabLabel
                $active={currentTab === 'VOLOP'}
                count={org.volops.length}
                icon={<Icon name="hand" colorName="white" size={24} />}
              >
                {getText('Volunteer opportunities')}
              </ViewOrgPageListingsTabsTabLabel>
              <Icon
                name={`chevron-${currentTab === 'VOLOP' ? 'up' : 'down'}`}
                colorName="darkContentGrey"
                size={24}
              />
            </StyledSummary>
          }
        >
          <ViewOrgPageTruncatedListings
            listingType="VOLOP"
            listings={org.volops}
          />
        </StyledAccordionItem>
      )}

      {org.events.length > 0 && (
        <StyledAccordionItem
          defaultOpen={defaultTab === 'EVENT'}
          name="opportunities"
          onToggle={(open) =>
            setCurrentTab((prevTab) => {
              if (open) return 'EVENT';
              if (prevTab === 'EVENT') return null;
              return prevTab;
            })
          }
          header={
            <StyledSummary>
              <ViewOrgPageListingsTabsTabLabel
                $active={currentTab === 'EVENT'}
                count={org.events.length}
                icon={<Icon name="star-calendar" colorName="white" size={24} />}
              >
                {getText('Events')}
              </ViewOrgPageListingsTabsTabLabel>
              <Icon
                name={`chevron-${currentTab === 'EVENT' ? 'up' : 'down'}`}
                colorName="darkContentGrey"
                size={24}
              />
            </StyledSummary>
          }
        >
          <ViewOrgPageTruncatedListings
            listingType="EVENT"
            listings={org.events}
          />
        </StyledAccordionItem>
      )}
    </Accordion>
  );
}
